import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import Toolbar from 'components/toolbar'
import DefaultPostsFilterFields from './posts-filter-fields'
import { Checkbox } from 'lp-components'
import * as Types from 'types'
import { get, isEqual, isEmpty, mapValues } from 'lodash'
import { deepCount } from 'utils'
import classnames from 'classnames'

const propTypes = {
  filterOptions: PropTypes.object.isRequired,
  applyFilter: PropTypes.func.isRequired,
  resetFilter: PropTypes.func.isRequired,
  appliedFilters: PropTypes.object.isRequired,
  selectedFilters: PropTypes.object.isRequired,
  filterFieldsComponent: PropTypes.elementType,
  filterToolbarVariant: PropTypes.string,
}

const defaultProps = {
  filterFieldsComponent: DefaultPostsFilterFields,
  filterToolbarVariant: 'default',
}

function PostsFilterToolbar({
  selectedFilters,
  appliedFilters,
  filterFieldsComponent: FilterFieldsComponent,
  filterToolbarVariant,
  applyFilter,
  ...rest
}) {
  const isFilterUnchanged = useCallback(
    (filterName) =>
      isEqual(
        get(appliedFilters, filterName),
        get(selectedFilters, filterName)
      ),
    [appliedFilters, selectedFilters]
  )

  const isFilterEmpty = useCallback(
    (filterName) => isEmpty(get(selectedFilters, filterName)),
    [selectedFilters]
  )

  const appliedFilterCounts = useMemo(
    () => mapValues(appliedFilters, deepCount),
    [appliedFilters]
  )

  const getAppliedFilterCount = useCallback(
    (filterName) => get(appliedFilterCounts, filterName),
    [appliedFilterCounts]
  )

  const [openFilterId, setOpenFilterId] = useState(null)

  const closeFilter = useCallback(
    (id) => {
      if (id !== openFilterId) return
      return setOpenFilterId(null)
    },
    [openFilterId]
  )

  return (
    <div
      className={classnames('filter-content', {
        'filter-open': !!openFilterId,
        'learning-hub-discussion-filters':
          filterToolbarVariant === 'learningHubPills',
      })}
    >
      <Toolbar
        className={classnames('filter-wrapper filters', {
          'learning-hub-discussion-filters__toolbar':
            filterToolbarVariant === 'learningHubPills',
        })}
        aria-label="Discussions Filtering"
        aria-controls="discussion-posts"
        data-cy="discussion-filters"
      >
        {filterToolbarVariant === 'learningHubPills' ? (
          <div className="learning-hub-filter-bar">
            <p className="learning-hub-filter-bar__label">
              <strong>Filter By:</strong>
            </p>
            <FilterFieldsComponent
              getAppliedFilterCount={getAppliedFilterCount}
              isFilterEmpty={isFilterEmpty}
              isFilterUnchanged={isFilterUnchanged}
              openFilterId={openFilterId}
              openFilter={setOpenFilterId}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              {...rest}
            />
            <div className="learning-hub-filter-bar__bookmark">
              <Field
                name={Types.BOOKMARK_FILTER_NAME}
                component={Checkbox}
                label="Bookmarked"
                onChange={() => {
                  setTimeout(applyFilter, 0)
                }}
              />
            </div>
          </div>
        ) : (
          <React.Fragment>
            <p>
              <strong>Filter By:</strong>
            </p>
            <FilterFieldsComponent
              getAppliedFilterCount={getAppliedFilterCount}
              isFilterEmpty={isFilterEmpty}
              isFilterUnchanged={isFilterUnchanged}
              openFilterId={openFilterId}
              openFilter={setOpenFilterId}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              {...rest}
            />
          </React.Fragment>
        )}
      </Toolbar>
    </div>
  )
}

PostsFilterToolbar.propTypes = propTypes
PostsFilterToolbar.defaultProps = defaultProps

export default PostsFilterToolbar
